/**
 * useContentTopic — real-time single-doc subscription for one topic in
 * `projects/{projectId}/topic_backlog/{topicId}`.
 *
 * Mirrors the single-doc pattern of `useAuthor` (see hooks/useAuthor.ts):
 * returns `{ topic, loading, error }`, gracefully handles missing inputs
 * and missing docs (returns `topic: null` rather than throwing).
 *
 * Used by `TopicDetailRoutePage` to drive the standalone Topic route
 * (Phase 1 of the topic-route shell rollout).
 */

import { useEffect, useState } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import type { ContentTopic } from '../types';

export interface UseContentTopicReturn {
  topic: ContentTopic | null;
  loading: boolean;
  error: Error | null;
}

export function useContentTopic(
  projectId: string | null | undefined,
  topicId: string | null | undefined,
): UseContentTopicReturn {
  const [topic, setTopic] = useState<ContentTopic | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (!projectId || !topicId) {
      setTopic(null);
      setLoading(false);
      setError(null);
      return;
    }

    setLoading(true);
    setError(null);

    const db = CoreFirebaseService.getInstance().firestore;
    const ref = doc(db, 'projects', projectId, 'topic_backlog', topicId);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setTopic(null);
        } else {
          // Persist `id` for downstream consumers — Firestore data() does
          // not include the doc id, but every TopicBacklog write also
          // mirrors `id` into the data, so we trust whichever is present
          // and fall back to the snapshot id.
          const data = snap.data() as ContentTopic;
          setTopic({ ...(data as object), id: data.id ?? snap.id } as ContentTopic);
        }
        setLoading(false);
      },
      (err) => {
        setError(err as Error);
        setLoading(false);
      },
    );

    return () => unsub();
  }, [projectId, topicId]);

  return { topic, loading, error };
}
